import { Component } from '@angular/core';

import { AccountMode } from '../app-launcher.models';
import { AppLauncherStateService } from '../app-launcher-state.service';
import { BetaAuthService } from '../services/beta-auth.service';
import { FirebaseAuthService } from '../services/firebase-auth.service';

@Component({
	selector: 'sd-account',
	templateUrl: './account.component.html',
})
export class AccountComponent {
	public email = this.state.accountEmail;
	public resetEmail = this.state.accountEmail;
	public password = '';
	public error = '';
	public busy = false;
	public resetRequestOpen = false;
	public resetSent = false;

	constructor(
		public readonly state: AppLauncherStateService,
		private readonly betaAuth: BetaAuthService,
		private readonly firebaseAuth: FirebaseAuthService,
	) {}

	public get copy(): string {
		return this.state.accountMode === 'sign-in'
			? 'Sign in to attach this deployment to your App Launcher account.'
			: 'Create App Launcher credentials for the Labs preview.';
	}

	public get showingResetConfirm(): boolean {
		return Boolean(this.state.passwordResetToken);
	}

	public get showingResetRequest(): boolean {
		return this.resetRequestOpen && !this.showingResetConfirm;
	}

	public setMode(mode: AccountMode): void {
		this.state.setAccountMode(mode);
		this.resetRequestOpen = false;
		this.resetSent = false;
		this.error = '';
	}

	public openForgotPassword(): void {
		this.resetEmail = this.email || this.state.accountEmail;
		this.resetRequestOpen = true;
		this.resetSent = false;
		this.error = '';
	}

	public backToSignIn(): void {
		this.resetRequestOpen = false;
		this.resetSent = false;
		this.error = '';
		this.state.setAccountMode('sign-in');
	}

	public async submit(): Promise<void> {
		this.error = '';
		this.busy = true;
		try {
			const user =
				this.state.accountMode === 'sign-in'
					? await this.betaAuth.login(this.email, this.password)
					: await this.betaAuth.register(this.email, this.password);
			this.state.completeLogin(user.email);
		} catch (err) {
			this.error =
				err instanceof Error ? err.message : 'Authentication failed.';
		} finally {
			this.busy = false;
		}
	}

	public onResetEmailInput(event: Event): void {
		const target = event.target as HTMLInputElement | null;
		if (target) {
			this.resetEmail = target.value;
		}
	}

	public onNewPasswordInput(event: Event): void {
		const target = event.target as HTMLInputElement | null;
		if (target) {
			this.password = target.value;
		}
	}

	public async sendResetLink(): Promise<void> {
		this.error = '';
		this.busy = true;
		try {
			await this.betaAuth.forgotPassword(this.resetEmail);
			this.email = this.resetEmail;
			this.resetSent = true;
		} catch (err) {
			this.error =
				err instanceof Error
					? err.message
					: 'Unable to send reset email.';
		} finally {
			this.busy = false;
		}
	}

	public async confirmReset(): Promise<void> {
		this.error = '';
		this.busy = true;
		try {
			await this.betaAuth.resetPassword(
				this.state.passwordResetToken,
				this.password,
			);
			this.state.completePasswordReset();
			this.password = '';
		} catch (err) {
			this.error =
				err instanceof Error
					? err.message
					: 'Unable to reset the password.';
		} finally {
			this.busy = false;
		}
	}

	public async google(): Promise<void> {
		this.error = '';
		this.busy = true;
		try {
			const user = await this.firebaseAuth.signInWithGoogle();
			this.state.completeLogin(user.email);
		} catch (err) {
			this.error =
				err instanceof Error ? err.message : 'Google sign-in failed.';
		} finally {
			this.busy = false;
		}
	}
}
